import { useIntl } from '@umijs/max';
import { DatePicker, Empty, Input, Select, Table } from 'antd';
import { createStyles } from 'antd-style';
import dayjs, { type Dayjs } from 'dayjs';
import React from 'react';
import CopyText from '@/components/CopyText';
import UserCell from '@/components/UserCell';
import { useFillTable, useUserMap } from '@/hooks';
import {
  adminGetUserCreditLedger,
  adminListGlobalLedger,
  type CreditLedgerEntry,
  type LedgerPage,
} from '@/services/auth/api';

const CALLER_OPTIONS = [
  'canvas_image_freegen',
  'canvas_image_relight',
  'canvas_image_multi_view',
  'canvas_image_inpaint',
  'canvas_image_outpaint',
  'canvas_image_matting',
  'canvas_image_hd',
  'canvas_image_panorama',
  'canvas_image_sheet_shot_grid',
  'canvas_image_sheet_expression',
  'canvas_image_sheet_action_pose',
  'canvas_image_sheet_outfit',
  'canvas_image_sheet_lighting',
  'canvas_image_sheet_hand_detail',
  'canvas_video_freegen',
  'canvas_audio_freegen',
  'character_canonical',
  'character_shot',
  'character_sheet',
  'scene_canonical',
  'scene_sheet',
  'scene_sheet_kind',
  'scene_extra_view',
  'scene_panorama',
  'project_cover',
  'storyboard_frame',
  'storyboard_video',
  'storyboard_audio',
  'project-parse',
  'parse-detect-marker',
  'parse-infer-meta',
  'parse-scene-boundaries',
  'parse-normalize-scene',
  'learn-parse-dsl',
  'extract-characters',
  'extract-scenes',
  'enrich-project',
  'character-prompts',
  'character-sheet-cells',
  'scene-sheet-cells',
  'storyboard',
  'storyboard-gen',
  'storyboard-shot-regenerate',
  'storyboard-frame-prompt',
  'storyboard-video-prompt',
  'reverse-prompt-image',
  'reverse-prompt-video',
  'canvas-text-rewrite',
  'canvas-prompt-optimize',
  'novel-adapt',
  'oral-generate',
  'canonicalize-scenes',
  'video-prompt',
  'agent-chat',
  'skill-compile',
];

const useStyles = createStyles(({ token }) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  toolbar: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    gap: 8,
  },
  compactTable: {
    '& .ant-table-cell': { padding: '8px 10px !important' },
    '& .ant-table-thead > tr > th': {
      padding: '8px 10px !important',
      fontSize: 12,
    },
  },
  refId: {
    fontFamily: token.fontFamilyCode,
    fontSize: 11,
    color: token.colorTextTertiary,
  },
  delta: {
    fontWeight: 600,
    color: token.colorErrorText,
  },
}));

export interface LedgerPanelProps {
  /** 传 hex 锁定单用户;不传 = 全局视图(支持 userId 搜索) */
  scopedUserId?: string;
  /** 表格滚动高度 */
  scrollY?: number;
  /** 是否显示用户列(scope 模式自动隐藏) */
  showUserColumn?: boolean;
  /** 点击用户列回调,接收 userId 数字 */
  onOpenUser?: (userId: number) => void;
}

const LedgerPanel: React.FC<LedgerPanelProps> = ({
  scopedUserId,
  scrollY,
  showUserColumn = true,
  onOpenUser,
}) => {
  const { styles } = useStyles();
  const intl = useIntl();
  const t = (id: string, fallback: string) =>
    intl.formatMessage({ id, defaultMessage: fallback });

  const [data, setData] = React.useState<LedgerPage | null>(null);
  const [loading, setLoading] = React.useState(false);
  const [callers, setCallers] = React.useState<string[]>([]);
  const [range, setRange] = React.useState<[Dayjs | null, Dayjs | null]>([
    null,
    null,
  ]);
  const [userIdInput, setUserIdInput] = React.useState('');
  const [userIdFilter, setUserIdFilter] = React.useState('');
  const [page, setPage] = React.useState(1);
  const [pageSize, setPageSize] = React.useState(50);

  // 未显式给 scrollY(页面级,非 Modal)时让表格填满视口、行少不塌
  const fillTable = scrollY === undefined;
  const { scrollY: fillScrollY, wrapperProps } = useFillTable(
    data?.entries?.length,
  );
  const effectiveScrollY = scrollY ?? fillScrollY;

  const fetch = React.useCallback(async () => {
    setLoading(true);
    try {
      const q = {
        type: 'spend' as const,
        caller: callers.length ? callers : undefined,
        from: range[0] ? range[0].toISOString() : undefined,
        to: range[1] ? range[1].toISOString() : undefined,
        page,
        pageSize,
      };
      const res = scopedUserId
        ? await adminGetUserCreditLedger(scopedUserId, q)
        : await adminListGlobalLedger({
            ...q,
            userId: userIdFilter || undefined,
          });
      if (res?.success && res.data) setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [callers, range, page, pageSize, scopedUserId, userIdFilter]);

  React.useEffect(() => {
    void fetch();
  }, [fetch]);

  const { userMap } = useUserMap((data?.entries || []).map((e) => e.userId));

  const callerLabel = (c?: string) => {
    if (!c) return '—';
    return intl.formatMessage({
      id: `ledger.caller.${c}`,
      defaultMessage: c,
    });
  };

  const columns = [
    {
      title: t('ledger.time', '时间'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 160,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm:ss'),
    },
    ...(scopedUserId || !showUserColumn
      ? []
      : [
          {
            title: t('ledger.user', '用户'),
            dataIndex: 'userId',
            key: 'user',
            width: 200,
            render: (uid: string) => (
              <UserCell userId={uid} userMap={userMap} onOpen={onOpenUser} />
            ),
          },
        ]),
    {
      title: t('ledger.caller', '任务类型'),
      dataIndex: 'caller',
      key: 'caller',
      width: 140,
      render: (c?: string) => callerLabel(c),
    },
    {
      title: t('ledger.refId', '任务 ID'),
      dataIndex: 'refId',
      key: 'refId',
      render: (v?: string) =>
        v ? (
          <CopyText
            text={v}
            tooltips={[v, t('component.copyText.copied', '已复制')]}
            className={styles.refId}
          >
            {v.length > 28 ? `${v.slice(0, 14)}…${v.slice(-10)}` : v}
          </CopyText>
        ) : (
          '—'
        ),
    },
    {
      title: t('ledger.amount', '扣费'),
      dataIndex: 'delta',
      key: 'delta',
      width: 100,
      align: 'right' as const,
      render: (v: number) => <span className={styles.delta}>{v} c</span>,
    },
    {
      title: t('ledger.balance', '余额'),
      dataIndex: 'balanceAfter',
      key: 'balanceAfter',
      width: 100,
      align: 'right' as const,
      render: (v: number) => `${v.toLocaleString()} c`,
    },
  ];

  return (
    <div className={styles.root}>
      <div className={styles.toolbar}>
        <Select
          mode="multiple"
          allowClear
          maxTagCount="responsive"
          placeholder={t('ledger.filterCaller', '任务类型')}
          value={callers}
          onChange={(v) => {
            setCallers(v);
            setPage(1);
          }}
          style={{ minWidth: 220 }}
          options={CALLER_OPTIONS.map((c) => ({
            value: c,
            label: callerLabel(c),
          }))}
        />
        <DatePicker.RangePicker
          showTime
          value={range}
          onChange={(v) => {
            setRange([v?.[0] ?? null, v?.[1] ?? null]);
            setPage(1);
          }}
          placeholder={[
            t('ledger.filterDate', '时间区间'),
            t('ledger.filterDate', '时间区间'),
          ]}
        />
        {!scopedUserId && (
          <Input
            placeholder={t('ledger.filterUser', '用户搜索')}
            value={userIdInput}
            onChange={(e) => setUserIdInput(e.target.value)}
            onPressEnter={(e) => {
              setUserIdFilter(e.currentTarget.value.trim());
              setPage(1);
            }}
            allowClear
            style={{ width: 240 }}
          />
        )}
      </div>

      <div {...(fillTable ? wrapperProps : {})}>
        <Table<CreditLedgerEntry>
          size="small"
          className={styles.compactTable}
          rowKey={(r) => r.id || `${r.userId}-${r.createdAt}`}
          loading={loading}
          dataSource={data?.entries || []}
          columns={columns}
          scroll={effectiveScrollY ? { y: effectiveScrollY } : undefined}
          locale={{
            emptyText: (
              <Empty
                image={Empty.PRESENTED_IMAGE_SIMPLE}
                description={t('ledger.empty', '暂无消费记录')}
              />
            ),
          }}
          pagination={{
            current: page,
            pageSize,
            total: data?.total ?? 0,
            showSizeChanger: true,
            pageSizeOptions: [20, 50, 100, 200],
            onChange: (p, ps) => {
              setPage(p);
              setPageSize(ps);
            },
          }}
        />
      </div>
    </div>
  );
};

export default LedgerPanel;
